import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Copy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageMeta } from "@/components/common/PageMeta";
import { PageHero } from "@/components/common/PageHero";
import { WHO_SHOULD_APPLY, COMMUNITY_CODE, HERO_ALT, PROJECT_FAMILIES, PREFERRED_CONTRIBUTION } from "@/lib/content";

const DISCIPLINES = [
  "AI and machine learning", "Neuroscience and cognition",
  "Quantum computing systems", "AI cyber security",
  "Physics and complex systems",
  "Mathematics and information theory", "Signal, media and perception",
  "Software and systems engineering", "Distributed trust and data integrity", "Product, IP and commercialisation",
  "Digital marketing and growth", "Venture capital and fundraising",
  "Blockchain and crypto development", "Data centre operations and management",
];
const DRAFT_KEY = "dbrf_network_draft";
const EMPTY = {
  full_name: "", email: "", country: "", timezone: "", title: "", primary_discipline: "",
  additional_disciplines: [], methods: [], summary: "", contribution_areas: "",
  link_linkedin: "", link_orcid: "", link_scholar: "", link_github: "", link_website: "", link_cv: "",
  selected_work: "", collaboration_pref: "", availability: "", languages: "",
  project_families: [], evidence_of_work: "", nda_experience: "", preferred_contribution: [],
  public_optin: false, privacy_consent: false, company: "",
};

function Input({ label, name, value, onChange, type = "text", required, textarea, placeholder }) {
  const C = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-navy">{label}{required && <span className="text-[var(--error)]"> *</span>}</span>
      <C type={type} value={value} onChange={(e) => onChange(name, e.target.value)} data-testid={`network-${name}`} rows={textarea ? 4 : undefined} placeholder={placeholder}
        className="w-full rounded-xl border border-[var(--line)] bg-[var(--paper)] px-4 py-2.5 text-sm outline-none focus:border-[var(--cobalt)]" />
    </label>
  );
}

export default function JoinTheNetwork() {
  const [form, setForm] = useState(() => {
    try { return { ...EMPTY, ...JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}") }; } catch { return EMPTY; }
  });
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); }, [form]);
  const set = (n, v) => setForm((f) => ({ ...f, [n]: v }));
  const toggle = (n, v) => setForm((f) => ({ ...f, [n]: f[n].includes(v) ? f[n].filter((x) => x !== v) : [...f[n], v] }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = [];
    if (!form.full_name) errs.push("Full name is required");
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) errs.push("A valid email is required");
    if (!form.country) errs.push("Country is required");
    if (!form.title) errs.push("Professional title is required");
    if (!form.primary_discipline) errs.push("Primary discipline is required");
    if (!form.summary) errs.push("A short professional summary is required");
    if (!form.privacy_consent) errs.push("Privacy consent is required");
    setErrors(errs);
    if (errs.length) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/network-applications", form);
      setResult(data);
      localStorage.removeItem(DRAFT_KEY);
    } catch (err) {
      if (!err.response) {
        toast.error("We couldn't reach the server. Please check your connection and try again.");
      } else if (err.response.status === 429) {
        toast.error("Too many attempts just now. Please wait a minute and try again.");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Your application could not be submitted. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <>
        <PageMeta title="Application received" />
        <section className="container-dbrf py-24 lg:py-32">
          <div className="mx-auto max-w-xl rounded-3xl border border-[var(--line)] bg-[var(--paper)] p-10 text-center" data-testid="network-success">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--success)]/15 text-[var(--success)]"><Check size={26} /></div>
            <h1 className="mt-6 font-display text-3xl font-bold text-navy">Application received</h1>
            <p className="mt-3 text-[var(--mineral)]">Joining the network is an application to be considered for relevant collaboration — not a promise of employment or project allocation.</p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-[var(--ivory)] px-4 py-2">
              <span className="font-mono text-sm text-navy" data-testid="network-reference">{result.reference}</span>
              <button onClick={() => { navigator.clipboard?.writeText(result.reference); toast.success("Reference copied"); }} aria-label="Copy reference" className="text-[var(--cobalt)]"><Copy size={14} /></button>
            </div>
            <div className="mt-8"><Link to="/" className="text-sm font-semibold text-[var(--cobalt)] link-underline">Return home</Link></div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageMeta title="Join the network" description="Bring depth. Work across boundaries." />
      <PageHero
        eyebrow="Join the network"
        title="Bring depth. Work across boundaries."
        intro="DBRF is developing a professional network for specialists who can contribute to difficult interdisciplinary work. Joining the network is an application to be considered for relevant collaboration — not a promise of employment or project allocation."
        image="/assets/09-join-the-network-hero.webp"
        alt={HERO_ALT["join-the-network"]}
        objectPosition="center 45%"
      />

      <section className="container-dbrf py-14 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-[1fr_300px]">
          <form onSubmit={submit} className="order-2 lg:order-1">
            {errors.length > 0 && (
              <div role="alert" className="mb-6 rounded-xl border border-[var(--error)]/30 bg-[var(--error)]/10 p-4" data-testid="network-error-summary">
                <ul className="list-inside list-disc text-sm text-[var(--error)]">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
              </div>
            )}
            <input type="text" name="company" value={form.company} onChange={(e) => set("company", e.target.value)} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

            <div className="grid gap-5 rounded-3xl border border-[var(--line)] bg-[var(--paper)] p-8">
              <div className="grid gap-5 sm:grid-cols-2">
                <Input label="Full name" name="full_name" value={form.full_name} onChange={set} required />
                <Input label="Email" name="email" type="email" value={form.email} onChange={set} required />
                <Input label="Country" name="country" value={form.country} onChange={set} required />
                <Input label="Time zone" name="timezone" value={form.timezone} onChange={set} placeholder="e.g. UTC+1" />
                <Input label="Current professional title" name="title" value={form.title} onChange={set} required />
                <label className="block">
                  <span className="mb-1.5 block text-sm font-medium text-navy">Primary discipline <span className="text-[var(--error)]">*</span></span>
                  <select value={form.primary_discipline} onChange={(e) => set("primary_discipline", e.target.value)} data-testid="network-primary_discipline" className="w-full rounded-xl border border-[var(--line)] bg-[var(--paper)] px-4 py-2.5 text-sm outline-none focus:border-[var(--cobalt)]">
                    <option value="">Select…</option>
                    {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </label>
              </div>
              <div>
                <span className="mb-2 block text-sm font-medium text-navy">Additional disciplines</span>
                <div className="flex flex-wrap gap-2">
                  {DISCIPLINES.map((d) => (
                    <button key={d} type="button" onClick={() => toggle("additional_disciplines", d)} data-testid={`network-adddisc-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`btn-transition rounded-full border px-3 py-1.5 text-xs ${form.additional_disciplines.includes(d) ? "border-[var(--cobalt)] bg-[var(--cobalt)] text-white" : "border-[var(--line)] text-navy hover:border-[var(--cobalt)]"}`}>{d}</button>
                  ))}
                </div>
              </div>
              <Input label="Short professional summary" name="summary" value={form.summary} onChange={set} textarea required />
              <Input label="Areas of contribution" name="contribution_areas" value={form.contribution_areas} onChange={set} textarea />
              <div className="grid gap-5 sm:grid-cols-2">
                <Input label="LinkedIn" name="link_linkedin" value={form.link_linkedin} onChange={set} placeholder="https://" />
                <Input label="ORCID" name="link_orcid" value={form.link_orcid} onChange={set} placeholder="https://" />
                <Input label="Google Scholar" name="link_scholar" value={form.link_scholar} onChange={set} placeholder="https://" />
                <Input label="GitHub" name="link_github" value={form.link_github} onChange={set} placeholder="https://" />
                <Input label="Personal / institutional site" name="link_website" value={form.link_website} onChange={set} placeholder="https://" />
                <Input label="Secure CV link" name="link_cv" value={form.link_cv} onChange={set} placeholder="https://" />
              </div>
              <p className="rounded-lg bg-[var(--ivory)] px-4 py-3 text-xs text-[var(--mineral)]">CV file uploads are not accepted in this version. Please provide a secure link instead.</p>
              <div>
                <span className="mb-2 block text-sm font-medium text-navy">Project families of interest</span>
                <div className="flex flex-wrap gap-2">
                  {PROJECT_FAMILIES.map((d) => (
                    <button key={d} type="button" onClick={() => toggle("project_families", d)} data-testid={`network-family-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`btn-transition rounded-full border px-3 py-1.5 text-xs ${form.project_families.includes(d) ? "border-[var(--cobalt)] bg-[var(--cobalt)] text-white" : "border-[var(--line)] text-navy hover:border-[var(--cobalt)]"}`}>{d}</button>
                  ))}
                </div>
              </div>
              <div>
                <span className="mb-2 block text-sm font-medium text-navy">Preferred contribution</span>
                <div className="flex flex-wrap gap-2">
                  {PREFERRED_CONTRIBUTION.map((d) => (
                    <button key={d} type="button" onClick={() => toggle("preferred_contribution", d)} data-testid={`network-contrib-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`btn-transition rounded-full border px-3 py-1.5 text-xs ${form.preferred_contribution.includes(d) ? "border-[var(--cobalt)] bg-[var(--cobalt)] text-white" : "border-[var(--line)] text-navy hover:border-[var(--cobalt)]"}`}>{d}</button>
                  ))}
                </div>
              </div>
              <Input label="Evidence of work in one selected skill" name="evidence_of_work" value={form.evidence_of_work} onChange={set} textarea placeholder="A link or short description of representative, shareable work." />
              <Input label="Experience working under NDA / confidential OEM conditions" name="nda_experience" value={form.nda_experience} onChange={set} />
              <div className="grid gap-5 sm:grid-cols-2">
                <Input label="Collaboration preference" name="collaboration_pref" value={form.collaboration_pref} onChange={set} />
                <Input label="Availability indication" name="availability" value={form.availability} onChange={set} />
                <Input label="Languages" name="languages" value={form.languages} onChange={set} />
                <Input label="Selected public work" name="selected_work" value={form.selected_work} onChange={set} />
              </div>
              <label className="flex items-start gap-3">
                <input type="checkbox" checked={form.public_optin} onChange={(e) => set("public_optin", e.target.checked)} data-testid="network-public_optin" className="mt-1 h-4 w-4 accent-[var(--cobalt)]" />
                <span className="text-sm text-navy">Opt in to a public profile (default off). Only opt-in profiles may be shown publicly.</span>
              </label>
              <label className="flex items-start gap-3">
                <input type="checkbox" checked={form.privacy_consent} onChange={(e) => set("privacy_consent", e.target.checked)} data-testid="network-privacy_consent" className="mt-1 h-4 w-4 accent-[var(--cobalt)]" />
                <span className="text-sm text-navy">I consent to DBRF processing this application. <span className="text-[var(--error)]">*</span></span>
              </label>
              <button type="submit" disabled={submitting} data-testid="network-submit" className="btn-transition inline-flex items-center justify-center gap-2 rounded-full bg-[var(--navy)] px-6 py-3 text-sm font-semibold text-white hover:bg-[var(--cobalt)] disabled:opacity-60">
                {submitting ? "Submitting…" : "Submit application"}
              </button>
            </div>
          </form>

          <aside className="order-1 space-y-6 lg:order-2 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-6">
              <p className="eyebrow mb-3">Who should apply</p>
              <ul className="space-y-2 text-sm text-[var(--mineral)]">{WHO_SHOULD_APPLY.map((w) => <li key={w} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--amber)]" />{w}</li>)}</ul>
            </div>
            <div className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-6">
              <p className="eyebrow mb-3">Code of collaboration</p>
              <ul className="space-y-2 text-sm text-[var(--mineral)]">{COMMUNITY_CODE.map((c) => <li key={c} className="flex gap-2"><Check size={14} className="mt-0.5 flex-shrink-0 text-[var(--success)]" />{c}</li>)}</ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
